package ai.wagemix.logic.analytics

import ai.wagemix.net.models.firebase.WMSubscriptionType
import co.touchlab.kermit.Severity
import com.fsryan.foundation.util.log.FSEventLogWriter

/**
 * The app-level state the [WMAnalyticsGate] adds to every event it forwards (planning/analytics-plan.md §4). Each
 * member is read when an event is logged, so it must be cheap and must not throw.
 */
interface WMAnalyticsContext {

    /** The login session's id, or null when nobody is signed in (`wm_login_session_id`). */
    val loginSessionId: String?

    /** The stable screen name of the current route (`screen`), or null before the first route is known. */
    val screen: String?

    /** Whether a user is signed in (`signed_in`). */
    val signedIn: Boolean

    /** The signed-in user's plan, or null when nobody is signed in or it is not known yet (`plan`, `none`). */
    val plan: WMSubscriptionType?

    /** The FS window size class of the window (`viewport`): `compact` … `extra_large`. */
    val viewport: String

    /** The colour mode in effect (`color_mode`): `light` or `dark`. */
    val colorMode: String
}

/**
 * The FSLog event writer between FSLog and the analytics writer (planning/analytics-plan.md §2). It:
 *
 * 1. forwards only the events of [WMAnalyticsCatalog]; every other event (the foundation's `UserAction`, the data
 *    layer's failures) is dropped;
 * 2. renames each forwarded event to its OpenPanel name ([WMAnalyticsCatalog.openPanelNameOf]);
 * 3. adds the [WMAnalyticsContext] properties to it. A property the event sets itself wins over the context's.
 *
 * Its id is the delegate's, so FSLog addresses the gate exactly as it would the analytics writer. Plain message logs
 * are not forwarded: analytics are events.
 */
class WMAnalyticsGate private constructor(
    private val delegate: FSEventLogWriter,
    private val context: WMAnalyticsContext,
): FSEventLogWriter() {

    override val id: String get() = delegate.id

    override fun log(severity: Severity, message: String, tag: String, throwable: Throwable?) = Unit

    override fun log(severity: Severity, eventName: String, richMessage: Map<String, Any>, throwable: Throwable?) {
        val openPanelName = WMAnalyticsCatalog.openPanelNameOf(eventName) ?: return
        delegate.log(
            severity = severity,
            eventName = openPanelName,
            richMessage = enriched(richMessage),
            throwable = throwable,
        )
    }

    private fun enriched(richMessage: Map<String, Any>): Map<String, Any> {
        return buildMap {
            context.loginSessionId?.let { put("wm_login_session_id", it) }
            context.screen?.let { put("screen", it) }
            put("signed_in", context.signedIn)
            put("plan", (context.plan ?: WMSubscriptionType.None).stringValue)
            put("viewport", context.viewport)
            put("color_mode", context.colorMode)
            putAll(richMessage)
        }
    }

    /** Anchors the factory. */
    companion object {

        /**
         * Creates a gate in front of [delegate].
         *
         * @param delegate the analytics writer the catalogued events go to
         * @param context the app-level state added to every forwarded event
         * @return the gate, to install with `preLoggingSetup(...)` in place of [delegate]
         */
        operator fun invoke(delegate: FSEventLogWriter, context: WMAnalyticsContext): WMAnalyticsGate {
            return WMAnalyticsGate(delegate = delegate, context = context)
        }
    }
}
